import { useState, useRef, DragEvent } from "react";
import { Upload, Film, X, Check, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadedFile, assignTakeNumbers, formatBytes, formatTime } from "@/lib/tape";
import {
  ALLOWED_EXTENSIONS,
  IOS_SAFARI_WARN_BYTES,
  LONG_CLIP_WARN_SEC,
  MAX_BATCH_SIZE_BYTES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES,
  formatBytesShort,
  isAllowedFormat,
  isIOSSafari,
  probeVideoMetadata,
} from "@/lib/upload-constraints";

interface Props {
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  onContinue: () => void;
}

type Status = "reading" | "preparing" | "ready";

export function UploadStage({ files, setFiles, onContinue }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statuses, setStatuses] = useState<Record<string, Status>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  // NOTE: do NOT revoke object URLs on unmount. The `files` state is owned by
  // the parent TapesPage and persists across the Upload → Review → Export
  // navigation. Revoking here breaks the video previews when the user clicks
  // Back from Review and then Continue again. URLs are revoked when a file is
  // explicitly removed (`removeFile`) or when the whole session resets
  // (`reset` in tapes.tsx).

  async function handleFiles(list: FileList | File[]) {
    const incoming = Array.from(list);
    if (incoming.length === 0) return;

    setBusy(true);
    try {
      // 1) Format pre-filter (extension + MIME).
      const formatRejected: { name: string; reason: string }[] = [];
      const formatOk: File[] = [];
      for (const f of incoming) {
        if (!isAllowedFormat(f)) {
          formatRejected.push({
            name: f.name,
            reason: `Unsupported format. Allowed: ${ALLOWED_EXTENSIONS.map((e) => "." + e).join(", ")}.`,
          });
        } else {
          formatOk.push(f);
        }
      }

      // 2) Per-file size check.
      const sizeOk: File[] = [];
      const sizeRejected: { name: string; reason: string }[] = [];
      for (const f of formatOk) {
        if (f.size > MAX_FILE_SIZE_BYTES) {
          sizeRejected.push({
            name: f.name,
            reason: `This clip exceeds the ${formatBytesShort(MAX_FILE_SIZE_BYTES)} limit.`,
          });
        } else {
          sizeOk.push(f);
        }
      }

      // 3) Batch count check.
      const remainingSlots = Math.max(0, MAX_FILES - files.length);
      const batchCountAccepted = sizeOk.slice(0, remainingSlots);
      const batchCountRejected = sizeOk.slice(remainingSlots).map((f) => ({
        name: f.name,
        reason: `Maximum ${MAX_FILES} clips per batch.`,
      }));
      if (sizeOk.length > 0 && remainingSlots === 0) {
        toast.error(`Maximum ${MAX_FILES} clips per batch.`);
      }

      // 4) Batch byte budget check.
      const currentBytes = files.reduce((s, f) => s + f.sizeBytes, 0);
      let runningBytes = currentBytes;
      const batchByteAccepted: File[] = [];
      const batchByteRejected: { name: string; reason: string }[] = [];
      for (const f of batchCountAccepted) {
        if (runningBytes + f.size > MAX_BATCH_SIZE_BYTES) {
          batchByteRejected.push({
            name: f.name,
            reason: `Maximum upload batch size is ${formatBytesShort(MAX_BATCH_SIZE_BYTES)}.`,
          });
        } else {
          runningBytes += f.size;
          batchByteAccepted.push(f);
        }
      }

      // 5) Probe metadata for survivors.
      const accepted: UploadedFile[] = [];
      const probeRejected: { name: string; reason: string }[] = [];
      const longClipWarnings: string[] = [];

      for (const file of batchByteAccepted) {
        const id = crypto.randomUUID();
        setStatuses((s) => ({ ...s, [id]: "reading" }));
        try {
          const meta = await probeVideoMetadata(file);
          setStatuses((s) => ({ ...s, [id]: "preparing" }));
          const url = URL.createObjectURL(file);
          const lower = file.name.toLowerCase();
          const isSlate = lower.includes("slate");

          if (meta.durationSec > LONG_CLIP_WARN_SEC) {
            longClipWarnings.push(file.name);
          }

          accepted.push({
            id,
            file,
            name: file.name,
            sizeBytes: file.size,
            durationSec: meta.durationSec,
            url,
            isSlate,
            trimStart: 0,
            trimEnd: meta.durationSec,
            uploadProgress: 100,
            uploaded: true,
          });
          setStatuses((s) => ({ ...s, [id]: "ready" }));
        } catch (err) {
          setStatuses((s) => {
            const next = { ...s };
            delete next[id];
            return next;
          });
          probeRejected.push({
            name: file.name,
            reason: err instanceof Error ? err.message : "Could not read video.",
          });
        }
      }

      // 6) Commit accepted files atomically.
      if (accepted.length > 0) {
        setFiles((prev) => assignTakeNumbers([...prev, ...accepted]));
      }

      // 7) Surface user-facing messages.
      const allRejected = [
        ...formatRejected,
        ...sizeRejected,
        ...batchCountRejected,
        ...batchByteRejected,
        ...probeRejected,
      ];

      for (const r of allRejected) {
        toast.error(`${r.name}: ${r.reason}`);
      }

      if (longClipWarnings.length > 0) {
        toast.warning(
          `${longClipWarnings.length === 1 ? "1 clip is" : `${longClipWarnings.length} clips are`} longer than 20 minutes — exports may take a while.`,
        );
      }

      // 8) iOS Safari heavy-batch warning (non-blocking).
      const totalBytes = runningBytes;
      if (isIOSSafari() && totalBytes > IOS_SAFARI_WARN_BYTES) {
        toast.warning(
          "Large video batches may be unstable on iPhone/iPad. Desktop Chrome is recommended for large exports.",
        );
      }

      if (accepted.length === 0 && allRejected.length === 0) {
        toast.error("Please select video files");
      }
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  }

  function removeFile(id: string) {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return assignTakeNumbers(prev.filter((f) => f.id !== id));
    });
    setStatuses((s) => {
      const next = { ...s };
      delete next[id];
      return next;
    });
  }


  const inFlightIds = Object.entries(statuses).filter(
    ([, st]) => st !== "ready",
  );
  const canContinue = files.length > 0 && !busy && inFlightIds.length === 0;

  const acceptAttr = ALLOWED_EXTENSIONS.map((e) => "." + e).join(",") + ",video/mp4,video/quicktime,video/x-m4v";

  return (
    <div className="max-w-3xl mx-auto space-y-10">
      <div className="text-center space-y-3">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
          Upload your slate and scene files
        </h1>
        <p className="text-muted-foreground text-base">
          Files stay on your device — we trim and rename in the browser.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-16 md:py-20 text-center transition-all bg-card/40 ${
          dragOver
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-card/70"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-5">
          <Upload className="size-5" />
        </div>
        <p className="text-lg font-medium">Drag & drop video files</p>
        <p className="text-sm text-muted-foreground mt-1.5">
          MP4, MOV, or M4V · up to {formatBytesShort(MAX_FILE_SIZE_BYTES)} each · max {MAX_FILES} files
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-6"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Browse files
        </Button>
      </div>

      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-foreground">
              Selected files
            </h2>
            <span className="text-xs text-muted-foreground">
              {files.length} file{files.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
            {files.map((f) => {
              const st = statuses[f.id] ?? "ready";
              const isLong = f.durationSec > LONG_CLIP_WARN_SEC;
              return (
                <div key={f.id} className="p-4 flex items-center gap-4">
                  <div className="shrink-0 size-10 rounded-md bg-muted text-muted-foreground grid place-items-center">
                    <Film className="size-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{f.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span>{formatTime(f.durationSec)} · {formatBytes(f.sizeBytes)}</span>
                      {isLong && (
                        <span className="inline-flex items-center gap-1 text-warning">
                          <AlertTriangle className="size-3" />
                          Long clip
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="shrink-0 w-28 text-right">
                    {st === "ready" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-success">
                        <Check className="size-3" strokeWidth={3} />
                        Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Loader2 className="size-3 animate-spin" />
                        {st === "reading" ? "Reading video…" : "Preparing preview…"}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Remove file"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button
          size="lg"
          disabled={!canContinue}
          onClick={onContinue}
          className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? (
            <>
              <Loader2 className="size-4 animate-spin mr-2" />
              Reading videos…
            </>
          ) : (
            "Continue to review"
          )}
        </Button>
      </div>
    </div>
  );
}
